/**
 * The browse canvas's results-drawer POSITION - a rail, and nothing else.
 *
 * THE DEFECT THIS CLOSES. On a zero-result URL at 393x852
 * (`/listings/rent?min_price=500000000`) the drawer's box measured
 * `top=1008, height=227` - 156 px BELOW the bottom edge of an 852 px viewport,
 * with nothing on screen to grab and no way back. Three causes, coupled:
 *
 * 1. **The drawer had no height.** `Drawer.Content` was `fixed bottom-0
 *    left-0 right-0`, so its height was its CONTENT's height, while vaul
 *    positions a bottom drawer by translating it down
 *    `window.innerHeight x (1 - snap)` - i.e. it assumes the drawer is
 *    viewport-tall. A ~250 px empty state at `half` therefore lands its top
 *    at `852 - 250 + 852 x 0.45 = 985`. `MobileSheet` owns that half (see
 *    its `SHEET_VIEWPORT_HEIGHT` note); it is stated here because the
 *    geometry is WHY the position below has to be exact.
 * 2. **Position was split across two owners, and `null` was
 *    representable.** `activeSnapPoint: number | string | null` lived in a
 *    React `useState`, the transform lived in vaul, and the rendered state
 *    was re-derived by EQUALITY against the snap constants with a silent
 *    `'half'` fallback for everything else - including `null`. No reducer,
 *    no invariant, no test.
 * 3. **Disappearing had no cause and no return.** The sheet could end up
 *    off-screen for reasons nothing named, and nothing brought it back.
 *
 * Causes 1 and 2 are closed here and in `MobileSheet`: position is a reduced
 * state, `coerceSnap` is TOTAL over everything vaul's own type can report, and
 * nothing downstream ever sees the `null`.
 *
 * ## What LEFT this file, and why that is cause 3's real fix
 *
 * Cause 3 was first answered by making the disappearance a STATE: a
 * `{ kind: 'shown' | 'hiddenForPreview' }` union, a `previewOpened` /
 * `previewDismissed` event pair, and a `viewportLeftMobile` reset. Every arm
 * of it was right about its own case, and the drawer was still strandable,
 * because a stored answer to "is the drawer on screen" is a SECOND copy of a
 * fact the selection already holds - and two copies of one fact are two
 * things that can disagree. They did: `handleMarkerClick` dispatched
 * `previewOpened` on DESKTOP, where the drawer is unmounted, so the machine
 * went to `hiddenForPreview` for a view that did not exist; the card highlight
 * then replaced the marker selection, the popover closed, and nothing
 * dispatched the return.
 *
 * So the union, both preview events, the viewport reset and `INITIAL_SHEET_STATE`
 * are all DELETED. Openness is derived where the preview lives
 * (`resultsSheetIsOpen`, `listings-selection.ts`); what is left here is the
 * rail, which is the only thing that was ever a genuine piece of drawer state.
 * A stale rail is harmless BY CONSTRUCTION - `SheetPosition` has no member
 * meaning "gone", so every value this module can hold is a value a reader can
 * see and grab - which is why the viewport reset is not replaced by anything.
 *
 * The owner is still `useListingsMapState`, and `MobileSheet` is still a pure
 * view. That replaced the `mapInteractionCount` counter and the ref-guarded
 * effect that read it: a monotonic integer whose only meaning was "something
 * happened" is exactly the shape that cannot say what, or say it twice.
 */

import { assertNever } from '@letsgethome/lib/utils/enums';

/**
 * The three rails the sheet may rest on. `peek` is the handle plus the count
 * header; `half` is the resting position; `full` covers the map.
 */
export type SheetPosition = 'peek' | 'half' | 'full';

/**
 * Every position, in ascending order - the roster the reducer's tests
 * iterate so a fourth rail cannot be added without them seeing it.
 */
export const SHEET_POSITIONS = ['peek', 'half', 'full'] as const satisfies readonly SheetPosition[];

/**
 * The fraction of the viewport each rail leaves on screen - vaul's
 * `snapPoints` vocabulary, and the ONLY place those numbers are written.
 *
 * `satisfies Record<SheetPosition, number>` is the guard: a new position
 * without a fraction does not compile, and the sheet can never pass vaul a
 * snap point this table does not name.
 *
 * The e2e suite needs the lowest of these to state "at least a rail's worth of
 * drawer is on screen", and it CANNOT import it: the Playwright runner loads
 * specs through Node's type stripping, which refuses a `.ts` file under
 * `node_modules`, so a value import from a workspace package fails at run time.
 * `MobileSheet` therefore PUBLISHES the rendered roster to the DOM
 * (`data-snap-points`) and the spec reads the app's own numbers back - see its
 * note there.
 */
export const SNAP_FRACTION = {
  peek: 0.12,
  half: 0.55,
  full: 1,
} as const satisfies Record<SheetPosition, number>;

/**
 * What the LIST inside the drawer does with a touch at each rail — a COLUMN of
 * the rail table, because it is a property of the rail and of nothing else.
 *
 * ⚠ THE DEFECT THIS CLOSES (R4-3). The list was `overflow-y-auto` at EVERY
 * rail, i.e. a native SCROLL CONTAINER at every rail — and a touch's effective
 * `touch-action` is computed from the touched element only UP TO the nearest
 * scroll container. vaul's own `[data-vaul-drawer] { touch-action: none }`
 * sits on the drawer ABOVE the list, so it never entered that computation:
 * the browser took every gesture that began inside the list before vaul saw a
 * pointer. Measured on the hermetic fleet at 393×852, `/listings/rent`, drawer
 * at `half` (top y=383.4), one 300 px touch swipe up starting inside the list:
 * the drawer moved 383→0 AND `scrollTop` went 0→362 — ONE gesture consumed
 * twice. Chromium's emulation lets both run; iOS Safari CANCELS the pointer
 * stream the moment the native scroll starts, which is the shape the owner
 * reported — the list scrolls and the drawer does not move. The handle worked
 * for exactly the complementary reason: it is `touch-none` and it is not
 * inside the scroller.
 *
 * So the remedy is vaul's own documented one, and it is a fact about the RAIL:
 * below the top rail the list is not a scroll container at all and the whole
 * drawer is a drag surface; at the top rail the list scrolls, and a pull-down
 * at `scrollTop === 0` climbs vaul's scrollable-ancestor walk, reaches the
 * `role="dialog"` and collapses the drawer. All three legs were re-measured
 * with the table applied (`docs/public-web-ux-audit-round4-handoff.md` §1.2).
 *
 * ⚠ A mouse drag cannot see any of this, which is why the suite gained real
 * touch gestures with the fix: a mouse never starts a native scroll, so the
 * same drag inside the list has always dragged the drawer a little
 * (383→358, measured) whatever this table says.
 *
 * `satisfies Record<SheetPosition, RailListBehaviour>` is the compile-time
 * guard — a fourth rail owes a behaviour or the table does not build. The
 * runtime half, that exactly the TOP rail scrolls and it is derived from
 * `SHEET_POSITIONS`' own order rather than from the literal `'full'`, is
 * `results-sheet-state.test.ts`'s.
 */
export type RailListBehaviour = 'drags-drawer' | 'scrolls';

export const RAIL_LIST_BEHAVIOUR = {
  peek: 'drags-drawer',
  half: 'drags-drawer',
  full: 'scrolls',
} as const satisfies Record<SheetPosition, RailListBehaviour>;

/**
 * Where the sheet rests when nothing has told it otherwise - the initial
 * position, and the answer `coerceSnap` gives for a value outside the
 * contract (below). It is named rather than spelled `'half'` in three
 * places so "the resting position" is one fact.
 */
export const RESTING_POSITION: SheetPosition = 'half';

export type ResultsSheetEvent =
  /** vaul reported a new active snap point - every value its type admits. */
  | { readonly type: 'vaulSnapped'; readonly raw: number | string | null }
  /** A deliberate map gesture (drag/zoom) -> get out of the map's way. */
  | { readonly type: 'mapGesture' }
  /** The floating "Map" button -> the same place, asked for explicitly. */
  | { readonly type: 'mapButton' };

/**
 * vaul's `setActiveSnapPoint` reports `number | string | null`, and this is
 * the ONE place that type is allowed to exist.
 *
 * * a **number** is a fraction we gave it, so the nearest rail is exact;
 *   out-of-range numbers are still resolved by nearest distance, which is
 *   what makes the function total rather than partial-with-a-fallback;
 * * a **string** is vaul's px spelling (`'200px'`). This sheet's snap points
 *   are fractions, so a string is outside its contract and cannot arise -
 *   the branch exists because vaul's TYPE admits one, and it resolves to the
 *   resting position rather than to `null`;
 * * **`null`** is vaul saying it has no active snap point. It used to flow
 *   straight into a `===` chain whose final `else` silently meant `half`;
 *   here it means `half` *by declaration*, and nothing downstream ever sees
 *   the `null`.
 */
export function coerceSnap(raw: number | string | null): SheetPosition {
  const asNumber = typeof raw === 'number' ? raw : raw === null ? Number.NaN : Number(raw);
  if (!Number.isFinite(asNumber)) return RESTING_POSITION;

  let nearest: SheetPosition = SHEET_POSITIONS[0];
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const position of SHEET_POSITIONS) {
    const distance = Math.abs(SNAP_FRACTION[position] - asNumber);
    if (distance < nearestDistance) {
      nearest = position;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/**
 * The whole of the sheet's position logic.
 *
 * Read the arms as the owner's sentence: the drawer collapses out of the map's
 * way on a gesture, and otherwise rests where a drag left it. There is no arm
 * for the preview and none for the viewport, because neither is a position -
 * "is the drawer on screen" is derived from the marker preview, and the
 * `md+` unmount needs no reset when every value in the state's TYPE is a rail
 * a reader can see.
 *
 * ★ THE CURRENT RAIL IS DELIBERATELY UNREAD, and that is a claim rather than
 * an oversight: every event names its own destination, so the rail is a pure
 * function of the LAST EVENT. It was not, while `previewOpened` had to read the
 * position in order to remember it — which is precisely the remembering that
 * turned out to be a second copy of a fact the selection already held. The
 * parameter stays because `useReducer` supplies it and because a fifth event
 * may well need it.
 *
 * `assertNever` is the guard: a fourth event does not compile until an arm
 * exists, and there is no default that could invent a position for it.
 */
export function resultsSheetReducer(
  _position: SheetPosition,
  event: ResultsSheetEvent
): SheetPosition {
  switch (event.type) {
    case 'vaulSnapped':
      return coerceSnap(event.raw);

    case 'mapGesture':
    case 'mapButton':
      // From ANY rail, including `peek` itself (idempotent).
      return 'peek';

    default:
      return assertNever(event);
  }
}
